// Reception detail panel — RON-5469. Prototype region: `#detail-panel`.
//
// Same row shape as the list (§4) — there is no second fetch, and no "detail" endpoint. The panel
// renders the row the list already has.
//
// Two regions here carry the contract's sharpest rules: the compliance block has THREE states per
// line rather than two, and the action buttons are driven by the server's flags rather than by
// the row's status.

import { useTranslation } from "react-i18next";

import {
  COMPLIANCE_STATE,
  ROW_STATUS,
  getAvatarTone,
  getComplianceState,
  getInitials,
  getRowTone,
  getStatusLabelKey,
  pluralText,
} from "../receptionConstants.js";

const Row = ({ label, children }) => (
  <>
    <dt className="reception-kv__k">{label}</dt>
    <dd className="reception-kv__v">{children}</dd>
  </>
);

// THE THREE-STATE LINE.
//
// §4: "`hs_acknowledged` and `nda_signed` are ABSENT, not `false`. `false` would say 'we asked and
// they refused', which is a different fact about a person standing at a desk, and a red cross on
// your panel would state it as truth."
//
// So "not recorded" is rendered as its own neutral state — never a cross, never a tick, never a
// blank. A blank row would read as "nothing to report", which is the same lie in quieter clothes.
const ComplianceLine = ({ label, state }) => {
  const { t } = useTranslation("common");

  if (state === COMPLIANCE_STATE.NOT_RECORDED) {
    return (
      <Row label={label}>
        <span className="reception-kv__muted">{t("react_reception_not_recorded")}</span>
      </Row>
    );
  }

  const ok = state === COMPLIANCE_STATE.YES;

  return (
    <Row label={label}>
      <span className={ok ? "reception-kv__ok" : "reception-kv__bad"}>
        {ok ? "✓ " : "✗ "}
        {t(ok ? "react_reception_yes" : "react_reception_no")}
      </span>
    </Row>
  );
};

const ReceptionDetailPanel = ({
  row,
  isBusy,
  onClose,
  onCheckIn,
  onCheckOut,
  onToggleVip,
  onNotifyHost,
}) => {
  const { t } = useTranslation("common");

  if (!row) {
    return (
      <aside className="reception-detail reception-detail--empty">
        <p className="reception-detail__placeholder">{t("react_reception_select_a_visitor")}</p>
      </aside>
    );
  }

  const tone = getRowTone(row);

  return (
    <aside className="reception-detail" aria-label={t("react_reception_detail")}>
      <button
        type="button"
        className="reception-detail__close"
        onClick={onClose}
        aria-label={t("react_reception_close_details")}
      >
        ✕
      </button>

      <div className="reception-detail__head">
        <span className={`reception-avatar reception-avatar--${getAvatarTone(row.name)} reception-avatar--lg`}>
          {getInitials(row.name)}
        </span>
        <div>
          <h2 className="reception-detail__name">{row.name}</h2>
          <p className="reception-detail__sub">
            {[
              row.company,
              t(
                row.kind === "contractor"
                  ? "react_reception_kind_contractor"
                  : "react_reception_kind_visitor"
              ),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>

      <div className="reception-detail__status">
        <span className={`reception-pill reception-pill--${tone}`}>
          {tone === "overstay" && row.overstayBy != null
            ? pluralText(t, "react_reception_overstay_by", row.overstayBy)
            : t(getStatusLabelKey(row.status))}
        </span>
        {row.isVip && <span className="reception-tag reception-tag--vip">VIP</span>}
        {row.isWalkIn && <span className="reception-tag">{t("react_reception_walk_in")}</span>}
      </div>

      <section className="reception-detail__section">
        <h3>{t("react_reception_visit_details")}</h3>
        <dl className="reception-kv">
          <Row label={t("react_reception_col_host")}>{row.host || "—"}</Row>
          <Row label={t("date")}>{row.startDate}</Row>
          <Row label={t("react_reception_arrival")}>{row.arrivalTime || "—"}</Row>
          <Row label={t("react_reception_expected_out")}>{row.expectedOut || "—"}</Row>
          {row.checkedInAt && (
            <Row label={t("react_reception_checked_in_at")}>{row.checkedInAt}</Row>
          )}
          {row.checkedOutAt && (
            <Row label={t("react_reception_checked_out_at")}>{row.checkedOutAt}</Row>
          )}
          <Row label={t("react_reception_booking_code")}>{row.bookingCode || "—"}</Row>
          {/* One contractor visit can cover a crew of six, and the muster counts people rather
              than rows — so a party of more than one says so. */}
          {row.personnel > 1 && (
            <Row label={t("react_reception_party")}>
              {pluralText(t, "react_reception_personnel", row.personnel)}
            </Row>
          )}
          {/* `purpose` carries `work_description` for a contractor, so one field serves both. */}
          <Row label={t("react_reception_purpose")}>{row.purpose || "—"}</Row>
        </dl>
      </section>

      {row.contractor && (
        <section className="reception-detail__section">
          <h3>{t("react_reception_vetting")}</h3>
          <dl className="reception-kv">
            <Row label={t("react_reception_trade")}>{row.contractor.trade || "—"}</Row>
            <Row label={t("status")}>{row.contractor.status}</Row>
            <Row label={t("react_reception_vetted_until")}>
              {row.contractor.vettedUntil || "—"}
            </Row>
          </dl>
          {/* Said before the receptionist presses Check in, not after the server refuses: the
              person is standing at the desk, and being told why in advance is the difference
              between explaining and apologising. */}
          {row.contractor.isProvisional && (
            <p className="reception-detail__warn">{t("react_reception_provisional_warning")}</p>
          )}
        </section>
      )}

      <section className="reception-detail__section">
        <h3>{t("react_reception_compliance")}</h3>
        <dl className="reception-kv">
          <ComplianceLine
            label={t("react_reception_hs_form")}
            state={getComplianceState(row.compliance, "hsAcknowledged")}
          />
          <ComplianceLine
            label={t("react_reception_nda")}
            state={getComplianceState(row.compliance, "ndaSigned")}
          />
          <ComplianceLine
            label={t("react_reception_photo")}
            state={getComplianceState(row.compliance, "photoCaptured")}
          />
          <Row label={t("react_reception_badge")}>
            {row.compliance?.badgeNumber
              ? `${row.compliance.badgeNumber} · ${row.compliance.badgeState}`
              : t("react_reception_no_badge")}
          </Row>
        </dl>
      </section>

      <section className="reception-detail__section">
        <h3>{t("react_reception_actions")}</h3>
        <div className="reception-detail__actions">
          {/* §4: "can_check_in / can_check_out come from the server. Drive your buttons off them
              rather than inferring from status — the rules include things a row cannot show you."
              Disabled rather than hidden, so the control's absence is never mistaken for a
              rendering fault. */}
          <button
            type="button"
            className="reception-btn"
            disabled={!row.canCheckIn || isBusy}
            onClick={() => onCheckIn?.(row)}
          >
            {t("react_reception_check_in")}
          </button>
          <button
            type="button"
            className="reception-btn reception-btn--danger"
            disabled={!row.canCheckOut || isBusy}
            onClick={() => onCheckOut?.(row)}
          >
            {t("react_reception_check_out")}
          </button>
          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isBusy}
            onClick={() => onNotifyHost?.(row)}
          >
            {t("react_reception_notify_host")}
          </button>
          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isBusy}
            onClick={() => onToggleVip?.(row)}
          >
            {t(row.isVip ? "react_reception_remove_vip" : "react_reception_mark_vip")}
          </button>
        </div>

        {/* Not built, and said out loud rather than shown as a dead button. §9: badge printing is
            an integration item and `vcm.visitor.block` is a capability with no table behind it. */}
        {row.status === ROW_STATUS.CHECKED_IN && (
          <p className="reception-detail__note">{t("react_reception_badge_print_note")}</p>
        )}
      </section>
    </aside>
  );
};

export default ReceptionDetailPanel;
